import { useState, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Ban, CheckCircle, Minus, Plus, X } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
  OTHER_REASON,
  PRESET_REASONS,
  VOID_NOTE_MAX,
  composeVoidReason,
  type VoidReason,
} from '@/lib/void-reasons'

interface PinEntryModalProps {
  orderId: string
  itemId: string
  itemName: string
  quantity: number
  unitPrice: number
  onSuccess: (itemId: string) => void
  onClose: () => void
}

export function PinEntryModal({
  orderId,
  itemId,
  itemName,
  quantity,
  unitPrice,
  onSuccess,
  onClose,
}: PinEntryModalProps) {
  const { formatCurrency } = useCurrency()
  const [pin, setPin] = useState(['', '', '', ''])
  const [preset, setPreset] = useState<VoidReason | null>(PRESET_REASONS[0])
  const [custom, setCustom] = useState('')
  const [voidQty, setVoidQty] = useState(quantity > 0 ? 1 : 0)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [shake, setShake] = useState(false)
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  // Typing beats tapping: the moment there is text in the free-text field it
  // IS the reason, and the preset chips deselect. One field, one answer — no
  // state where a chip and a typed reason disagree about what gets filed.
  const usingCustom = custom.trim() !== ''
  // Defensive only: clearing the field restores the default chip, so today
  // there is no path to "no reason at all". It stays as a guard because the
  // day someone removes the preselected default, a void with a blank reason
  // must not be the thing that ships.
  const reasonMissing = !usingCustom && preset === null
  const effectiveReason = usingCustom
    ? composeVoidReason(OTHER_REASON, custom)
    : (preset ?? '')

  useEffect(() => {
    inputRefs[0].current?.focus()
  }, [])

  const voidMutation = useMutation({
    mutationFn: async () => {
      const pinStr = pin.join('')
      return apiClient.voidItem(orderId, itemId, {
        pin: pinStr,
        reason: effectiveReason,
        quantity: voidQty < quantity ? voidQty : undefined,
      })
    },
    onSuccess: (res) => {
      if (res.success) {
        const authorizedBy = (res.data as any)?.authorized_by || 'Manager'
        setSuccessMsg(`Voided ${voidQty} × ${itemName} by ${authorizedBy}`)
        setTimeout(() => {
          onSuccess(itemId)
        }, 1200)
      } else {
        triggerShake()
      }
    },
    onError: () => {
      triggerShake()
    },
  })

  const triggerShake = () => {
    setError('Invalid PIN. Try again.')
    setShake(true)
    setPin(['', '', '', ''])
    setTimeout(() => setShake(false), 500)
    setTimeout(() => inputRefs[0].current?.focus(), 100)
  }

  const handleDigit = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return
    const newPin = [...pin]
    newPin[index] = value
    setPin(newPin)
    setError('')

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const handleSubmit = () => {
    if (reasonMissing) {
      setError('Pick a reason or type your own')
      return
    }
    const pinStr = pin.join('')
    if (pinStr.length !== 4) {
      setError('Please enter a 4-digit PIN')
      return
    }
    voidMutation.mutate()
  }

  if (successMsg) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-popover text-popover-foreground rounded-2xl p-8 w-96 text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-xl font-bold text-foreground">Item Voided</h3>
          <p className="text-muted-foreground mt-2">{successMsg}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-popover text-popover-foreground rounded-2xl w-96 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-destructive/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-destructive" />
            <h3 className="font-bold text-destructive">Void Item</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Item info */}
        <div className="p-4 border-b">
          <div className="font-medium text-foreground">{itemName}</div>
          <div className="text-sm text-muted-foreground mt-1">
            Qty: {quantity} &times; {formatCurrency(unitPrice)} = {formatCurrency(quantity * unitPrice)}
          </div>
        </div>

        {/* Quantity selector -- only shown when item has qty > 1 */}
        {quantity > 1 && (
          <div className="p-4 border-b">
            <label className="text-sm font-medium text-foreground block mb-2">
              Void how many?
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setVoidQty((v) => Math.max(1, v - 1))}
                disabled={voidQty <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-[3rem] text-center text-lg font-bold tabular-nums">
                {voidQty}
              </span>
              <button
                type="button"
                onClick={() => setVoidQty((v) => Math.min(quantity, v + 1))}
                disabled={voidQty >= quantity}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                of {quantity}
              </span>
            </div>
            <div className="mt-1.5 text-sm font-medium text-destructive">
              Voiding: {formatCurrency(unitPrice * voidQty)}
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="p-4 border-b">
          <label className="text-sm font-medium text-foreground block mb-2">Reason</label>
          <div className="grid grid-cols-2 gap-2">
            {PRESET_REASONS.map((r, i) => {
              const selected = !usingCustom && preset === r
              // An odd count would strand the last chip in the left column;
              // let it span instead so the block ends on a clean edge.
              const spans = i === PRESET_REASONS.length - 1 && PRESET_REASONS.length % 2 === 1
              return (
                <button
                  key={r}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setPreset(r)
                    setCustom('')
                    setError('')
                  }}
                  className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                    spans ? 'col-span-2' : ''
                  } ${
                    selected
                      ? 'border-destructive bg-destructive/10 text-destructive font-medium'
                      : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {r}
                </button>
              )
            })}
          </div>

          {/* The last option is the field itself — no "Other" to tap first. */}
          <div className="mt-2">
            <input
              type="text"
              value={custom}
              maxLength={VOID_NOTE_MAX}
              onChange={e => {
                setCustom(e.target.value)
                // Typing IS choosing; drop the chip so only one reason is live.
                if (e.target.value.trim() !== '') setPreset(null)
                else setPreset(PRESET_REASONS[0])
                setError('')
              }}
              placeholder="Or type your own reason…"
              className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                usingCustom
                  ? 'border-destructive bg-destructive/10 text-foreground'
                  : 'border-input bg-background text-foreground'
              }`}
            />
            {usingCustom && (
              <div className="mt-1.5 flex items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Saved to the void log — write what you would want to read back later.
                </p>
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {custom.length}/{VOID_NOTE_MAX}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* PIN Input */}
        <div className="p-6">
          <label className="text-sm font-medium text-foreground block mb-3 text-center">
            Enter Manager PIN
          </label>
          <div className={`flex gap-3 justify-center ${shake ? 'animate-shake' : ''}`}>
            {pin.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleDigit(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-14 h-14 text-center text-2xl font-bold border-2 border-input bg-background text-foreground rounded-xl focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40"
              />
            ))}
          </div>
          {error && (
            <p className="text-destructive text-sm text-center mt-3">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleSubmit}
            disabled={pin.some(d => d === '') || reasonMissing || voidMutation.isPending}
          >
            {voidMutation.isPending ? 'Verifying...' : `Void ${voidQty === quantity ? 'All' : voidQty}`}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out 2; }
      `}</style>
    </div>
  )
}
